import { useEffect } from "react";

/**
 * The Metronic "wizard-2" jQuery plugin (public/assets/js/pages/custom/wizard/wizard-2.js)
 * only initializes once, on the browser's `document.ready` event, and the
 * <script> tag that defines it is only ever injected once per session
 * (see `loadScript` in helpers/function.js). In this single-page app, that
 * means only the *first* claim-detail-style page visited in a session gets
 * working wizard tabs/next/prev buttons - every subsequent one renders a
 * fresh #kt_wizard_v2 DOM that the (already-finished) jQuery init never saw,
 * so none of its data-ktwizard-type clicks do anything.
 *
 * Call this once per page mount. It polls the DOM (rather than depending on
 * each page's own loading-state variable, which differs from file to file)
 * until the wizard markup actually exists, then re-runs KTWizard2.init()
 * against it. Safe to call repeatedly: init() just re-queries
 * #kt_wizard_v2/#kt_form and builds a fresh widget bound to the current DOM.
 */
export const useInitWizard = () => {
  useEffect(() => {
    let cancelled = false;
    let attempts = 0;

    const tryInit = () => {
      if (cancelled) return;

      const wizardReady = document.getElementById("kt_wizard_v2");
      const pluginReady =
        window.KTWizard2 && typeof window.KTWizard2.init === "function";

      if (wizardReady && pluginReady) {
        try {
          window.KTWizard2.init();
        } catch (e) {
          // Nothing to recover from here - leave the page usable either way.
        }
        return;
      }

      attempts += 1;
      if (attempts < 50) {
        setTimeout(tryInit, 100);
      }
    };

    tryInit();

    return () => {
      cancelled = true;
    };
  }, []);
};
